import React from 'react';
import { makeStyles, Box, Link, Theme } from '@material-ui/core';
import clsx from 'clsx';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import { EntrepreneurProfileBody } from '../../types/types';
import EditPencil from '../../components/EditPencil';
import AvatarImage from '../../components/AvatarImage';
import { useEntContext } from '../../contexts';
import { StyledText } from '../../components/StyledText';
import DetectLinks from '../../components/DetectLinks';
import { tbd } from '../../lib/utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    borderRadius: 8,
    background: theme.palette.background.paper,
    border: '1px solid #DAD9DF',
    position: 'relative',
    padding: 20,
    [theme.breakpoints.up('md')]: {
      padding: 35
    },
    margin: '10px 0'
  },

  content: {
    '& svg': {
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1)
    },
    '& p': {
      maxWidth: '100%',
      overflowWrap: 'break-word',
      display: 'block',
      lineBreak: 'anywhere',
      overflow: 'hidden',
      maxHeight: '68px' // max 1 line - lineHeight for h2 = 140% = 34px
    },

    '& a': {
      textDecoration: 'none',
      color: theme.palette.text.primary
    }
  },

  address: {
    flexDirection: 'column',
    fontWeight: 500,
    fontSize: 22,
    margin: 0
  }
}));

interface EntrepreneurContactPanelProps {
  profile: EntrepreneurProfileBody
  editable: boolean
  openEdit?: () => void
}

const EntrepreneurContactPanel: React.FC<EntrepreneurContactPanelProps> = ({
  profile,
  editable,
  openEdit
}) => {
  const {
    contactName,
    contactTitle,
    email,
    phone,
    street1,
    street2,
    city,
    state,
    website
  } = profile;
  const styles = useStyles();
  const { getProfileImageProvider } = useEntContext();
  const profileImageProvider = getProfileImageProvider(profile.id);
  const addressPart1 = (street1 && city && state) ? `${street1}, ${city}, ${state}` : null;
  const addressPart2 = (street1 && street2 && city && state) ? `${street1}, ${street2}, ${city}, ${state}` : null;
  const addressString = addressPart2 || addressPart1;

  return (
    <Box className={styles.root}>
      {editable && <EditPencil onClick={openEdit} style={{ top: 10, right: 10, position: 'absolute' }} />}
      <Box display='flex' marginBottom='20px' flexWrap='wrap'>
        <AvatarImage size={80} profileImageProvider={profileImageProvider} />
        <Box display='flex' flexDirection='column' margin='10px'>
          <StyledText variant='h2' color={contactName ? 'textPrimary' : 'textSecondary'}>{contactName || 'Contact person name'}</StyledText>
          <StyledText variant='h3' color='textSecondary'>{contactTitle || 'Title'}</StyledText>
        </Box>
      </Box>
      <Box display='flex' marginBottom='15px' className={clsx(styles.content)}>
        <LocationOnOutlinedIcon />
        <StyledText color={addressString ? 'textPrimary' : 'textSecondary'}>{addressString || tbd}</StyledText>
      </Box>
      <Box display='flex' marginBottom='15px' className={clsx(styles.content)}>
        <LinkOutlinedIcon />
        <StyledText color={website ? 'textPrimary' : 'textSecondary'}><DetectLinks content={website || tbd} /></StyledText>
      </Box>
      <Box display='flex' marginBottom='15px' className={clsx(styles.content)}>
        <PhoneOutlinedIcon />
        <StyledText color={phone ? 'textPrimary' : 'textSecondary'}>
          {phone || tbd}
        </StyledText>
      </Box>
      <Box display='flex' marginBottom='15px' className={clsx(styles.content)}>
        <EmailOutlinedIcon />
        <Link underline='none' color='secondary' href={`emailto:${email}`}>
          <StyledText color={email ? 'textPrimary' : 'textSecondary'}>{email || tbd}</StyledText>
        </Link>
      </Box>
    </Box>
  );
};

export default EntrepreneurContactPanel;
